import { useState, useEffect } from 'react';
import { Save, MapPin, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const DEFAULT_FENCES = [
  { id: 'gf-001', name: 'Head Office', lat: '19.0760', lng: '72.8777', radius: 200, active: true },
  { id: 'gf-002', name: 'Branch - Pune', lat: '18.5204', lng: '73.8567', radius: 300, active: true },
];

export default function GeofencingSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [fences, setFences] = useState(DEFAULT_FENCES);

  useEffect(() => {
    const saved = data?.data?.settings?.geofences;
    if (saved?.length) setFences(saved);
  }, [data]);

  const update = (id, k, v) => setFences(fs => fs.map(f => f.id === id ? { ...f, [k]: v } : f));
  const addFence = () => setFences(fs => [...fs, { id: `gf-${Date.now()}`, name: 'New Location', lat: '', lng: '', radius: 100, active: true }]);

  const inputCls = "h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const handleSave = () => {
    mutate({ settings: { geofences: fences } }, {
      onSuccess: () => toast.success('Geofences saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-2xl">
      <PageHeader
        title="Geofencing"
        subtitle="Define office boundaries for GPS attendance"
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Geofencing' }]}
        actions={<Button size="sm" icon={Plus} onClick={addFence}>Add Location</Button>}
      />

      <div className="space-y-4">
        {fences.map(f => (
          <div key={f.id} className="card space-y-4 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[var(--accent)]"/>
                <input value={f.name} onChange={e => update(f.id,'name',e.target.value)}
                  className="text-[15px] font-bold text-[var(--text-primary)] bg-transparent border-none outline-none focus:ring-0 p-0"/>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)] cursor-pointer">
                  <input type="checkbox" checked={f.active} onChange={e => update(f.id,'active',e.target.checked)} className="rounded accent-[var(--accent)]"/>
                  Active
                </label>
                {fences.length > 1 && (
                  <button onClick={() => setFences(fs => fs.filter(x => x.id !== f.id))}
                    className="p-1.5 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)]">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Latitude</label>
                <input value={f.lat} onChange={e => update(f.id,'lat',e.target.value)} placeholder="e.g. 19.0760" className={`${inputCls} w-full`}/>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Longitude</label>
                <input value={f.lng} onChange={e => update(f.id,'lng',e.target.value)} placeholder="e.g. 72.8777" className={`${inputCls} w-full`}/>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">Radius (meters)</label>
                <input type="number" min="50" max="5000" value={f.radius} onChange={e => update(f.id,'radius',+e.target.value)} className={`${inputCls} w-full`}/>
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[var(--bg-muted)] text-[12px] text-[var(--text-tertiary)]">
              <MapPin size={12}/>
              <span>Employees must be within {f.radius}m of ({f.lat || '…'}, {f.lng || '…'}) to clock in</span>
            </div>
          </div>
        ))}
      </div>

      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Geofences'}</Button>
    </div>
  );
}
